import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Map,
  Users,
  Building2,
  TrendingUp,
  ArrowRight,
  MapPin,
  Package,
  TrendingDown,
  Handshake,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { useRequireAuth } from "../hooks/useAuth";
import { useAuthStore } from "../stores/authStore";
import { Spinner } from "../components/ui/Spinner";

export function Dashboard() {
  const navigate = useNavigate();
  const { isLoading } = useRequireAuth();
  const { user } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const stats = [
    {
      label: "Operatori Totali",
      value: "85,000+",
      icon: Users,
      color: "text-blue-400",
      bgColor: "bg-blue-500/20",
    },
    {
      label: "Hotel",
      value: "84,000+",
      icon: Building2,
      color: "text-[#284899]",
      bgColor: "bg-[#284899]/20",
    },
    {
      label: "DMO & Attrazioni",
      value: "1,600+",
      icon: MapPin,
      color: "text-emerald-400",
      bgColor: "bg-emerald-500/20",
    },
    {
      label: "Regioni Coperte",
      value: "20",
      icon: Map,
      color: "text-purple-400",
      bgColor: "bg-purple-500/20",
    },
  ];

  return (
    <div className="min-h-[calc(100vh-64px)] p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#284899]/20 via-card to-card p-8 border border-border hover:border-[#284899]/40 transition-colors duration-200"
        >
          <div className="relative z-10">
            <h1 className="text-3xl font-bold mb-2">
              Benvenuto, {user?.first_name || "Operatore"}! 👋
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Esplora la rete di oltre 85.000 operatori turistici italiani.
              Utilizza la mappa interattiva per trovare partner e opportunità
              di collaborazione.
            </p>
            <Button
              className="mt-6"
              onClick={() => navigate("/map")}
            >
              <Map className="h-4 w-4 mr-2" />
              Esplora la Mappa
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#284899]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="hover:border-white/10 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}
                    >
                      <stat.icon className={`h-6 w-6 ${stat.color}`} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stat.value}</p>
                      <p className="text-sm text-muted-foreground">
                        {stat.label}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Quick Actions - Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="h-full hover:border-[#284899]/30 transition-colors">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Map className="h-5 w-5 text-[#284899]" />
                  Ricerca Geo-Temporale
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Disegna un'area sulla mappa e filtra gli operatori per
                  categoria, tipologia e periodo. Trova i partner ideali per le
                  tue esigenze.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#284899]" />
                    Disegna aree personalizzate sulla mappa
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#284899]" />
                    Filtra per categoria e tipologia
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#284899]" />
                    Cerca per periodo temporale
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#284899]" />
                    Salva le ricerche preferite
                  </li>
                </ul>
                <Button
                  variant="secondary"
                  className="mt-6"
                  onClick={() => navigate("/map")}
                >
                  Inizia la Ricerca
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="h-full hover:border-blue-500/30 transition-colors">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Handshake className="h-5 w-5 text-blue-400" />
                  Networking Operatori
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Connettiti con altri operatori turistici per creare
                  collaborazioni, pacchetti congiunti e sinergie sul territorio.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                    Scopri operatori vicini o affini
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                    Invia richieste di collaborazione
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                    Gestisci partnership attive
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                    Ricevi notifiche in tempo reale
                  </li>
                </ul>
                <Button
                  variant="secondary"
                  className="mt-6"
                  onClick={() => navigate("/networking")}
                >
                  Esplora Networking
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Quick Actions - Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Card className="h-full hover:border-emerald-500/30 transition-colors">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-emerald-400" />
                  Pacchetti Turistici
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Crea pacchetti turistici completi con calendario interattivo.
                  Aggiungi eventi, hotel e attività, poi genera PDF stampabili.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    Calendario interattivo per eventi
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    Genera PDF professionale
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    Pacchetti congiunti con approvazione
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    CRUD completo dei pacchetti
                  </li>
                </ul>
                <Button
                  variant="secondary"
                  className="mt-6"
                  onClick={() => navigate("/packages")}
                >
                  Crea Pacchetto
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Card className="h-full hover:border-purple-500/30 transition-colors">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingDown className="h-5 w-5 text-purple-400" />
                  Destagionalizzazione
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Analizza i periodi a bassa domanda turistica e ricevi
                  suggerimenti per pianificare attività promozionali fuori stagione.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                    Analisi domanda per regione
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                    Identificazione bassa stagione
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                    Avvisi e suggerimenti operativi
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                    Heatmap stagionalità nazionale
                  </li>
                </ul>
                <Button
                  variant="secondary"
                  className="mt-6"
                  onClick={() => navigate("/seasonality")}
                >
                  Analizza Stagionalità
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Data Sources */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-[#284899]" />
                Dati Integrati
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                I dati provengono da fonti certificate e vengono aggiornati
                regolarmente per garantire informazioni accurate e affidabili.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex items-center justify-between p-3 rounded-lg overlay-solid-secondary border border-border">
                  <span className="text-sm">ENIT - Agenzia Nazionale Turismo</span>
                  <span className="text-xs text-muted-foreground">DMO, Attrazioni</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg overlay-solid-secondary border border-border">
                  <span className="text-sm">Netstorming</span>
                  <span className="text-xs text-muted-foreground">Hotel</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg overlay-solid-secondary border border-border">
                  <span className="text-sm">Fonti Regionali</span>
                  <span className="text-xs text-muted-foreground">Eventi, Attività</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}

